import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';
import { Spacing } from '@zep/ui';
import { cn } from '@zep/utils';
import { refineSaleType } from '../../shared/refineSaleType';
import { useExpectedOnetimePlanPrice, useExpectedPlanPrice, usePlanPricing, usePlanStatusType } from '../hooks';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../PlanPaymentSection/Providers';
import { PlanIcon } from '../shared/PlanIcon';
import { PlanTemplateType } from '../types';
import { getVatPreview, useConvertOnetimePeriodToText } from '../utils';
export const SelectedPlanInfo = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    isUpdate,
    prices,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    convertOnetimePeriodToText
  } = useConvertOnetimePeriodToText();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const maxCcu = watch('maxCcu');
  const planTemplateType = watch('planTemplateType');
  const subscriptionPeriod = watch('subscriptionPeriod');
  const onetimePeriod = watch('onetimePeriod');
  const isCustomOnetimePeriod = watch('isCustomOnetimePeriod');
  const {
    customMonthPrice,
    yearlyPrice,
    threeDayPrice,
    monthlyPrice,
    oneDayPrice
  } = usePlanPricing({
    maxCcu,
    planType,
    prices
  });
  const pricePerCcu = match(planTemplateType).with(PlanTemplateType.PRODUCT_SUBSCRIPTION, () => match(subscriptionPeriod).with('30', () => monthlyPrice).with('365', () => yearlyPrice).otherwise(() => monthlyPrice)).with(PlanTemplateType.PRODUCT_ONETIME, () => match(onetimePeriod).with('1D', () => oneDayPrice).with('3D', () => threeDayPrice).otherwise(() => customMonthPrice)).otherwise(() => customMonthPrice);
  return <div className="w-full border-b border-[#27262E14] bg-white px-[20px] py-[16px]" data-sentry-component="SelectedPlanInfo" data-sentry-source-file="SelectedPlanInfo.tsx">
      <div className="flex w-full items-center">
        <span className="text-body-2-m font-[500] text-[#27262EE5]">
          {t('subscription:payment.summaryCommon.plan', '선택한 플랜')}
        </span>
        <div className="ml-auto flex items-center gap-[4px]">
          {match(planType).with('basic', () => <PlanIcon className="bg-gradient-to-r from-[#24B6EC] to-[#1DCFAF]" />).with('pro', () => <PlanIcon className="bg-gradient-to-r from-[#588AFF] to-[#A572FF]" />).otherwise(() => null)}
          <span className={cn('text-[13px] font-[700]', {
          'text-mintDark': planType === 'basic',
          'text-primary': planType === 'pro'
        })}>
            {planType.toUpperCase()}
          </span>
        </div>
      </div>
      <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="SelectedPlanInfo.tsx" />
      <div className="flex w-full items-center">
        <span className="text-body-2-m font-[500] text-[#27262EE5]">
          {currencyString}
          {pricePerCcu.toLocaleString()} X {selectedPlanInfo.maxCcu}
          {t('subscription:payment.planSelect.people', '명')}
          {match([subscriptionPeriod, planTemplateType]).with(['365', PlanTemplateType.PRODUCT_SUBSCRIPTION], () => t('subscription:payment.summaryCommon.X12month', ' X 12개월')).otherwise(() => null)}
          {match([isCustomOnetimePeriod, planTemplateType]).with([true, PlanTemplateType.PRODUCT_ONETIME], () => ' X ' + convertOnetimePeriodToText(onetimePeriod || '')).otherwise(() => null)}
        </span>
        <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
          {currencyString + ' '}
          {selectedPlanInfo.originalPrice.toLocaleString()}
        </span>
      </div>
      {selectedPlanInfo.saleDiscountPrice > 0 && <>
          <Spacing size={10} />
          <div className="flex w-full items-center">
            <span className="text-body-2-m font-[500] text-[#27262EE5]">
              {(t as Function)(`subscription:pricing.plansCommon.${refineSaleType(selectedPlanInfo.saleType ?? '')}`, {
            maxCcu
          })}
            </span>
            <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
              - {currencyString + ' '}
              {selectedPlanInfo.saleDiscountPrice.toLocaleString()}
            </span>
          </div>
        </>}
      {match([planTemplateType, isUpdate]).with([PlanTemplateType.PRODUCT_SUBSCRIPTION, true], () => <SubscriptionUnusedPrice />).with([PlanTemplateType.PRODUCT_ONETIME, true], () => <OnetimeUnusedPrice />).otherwise(() => null)}
      {/* VAT row 노출 여부는 country/currency가 아니라 vatAmount > 0으로만 결정한다 (Gate E) */}
      <VatRow data-sentry-element="VatRow" data-sentry-source-file="SelectedPlanInfo.tsx" />
    </div>;
};
const SubscriptionUnusedPrice = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    isUpdate,
    selectedPlanInfo,
    spaceHashId,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const planTemplateType = watch('planTemplateType');
  const {
    미사용금액
  } = useExpectedPlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION,
    spaceHashId
  });
  const planStatusType = usePlanStatusType({
    planType
  });
  if (미사용금액 <= 0) {
    return null;
  }
  return <>
      <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="SelectedPlanInfo.tsx" />
      {match(planStatusType).with('DOWN_GRADE', () => <div className="flex w-full items-center">
            <span className="text-body-2-m font-[500] text-[#27262EE5]">
              {t('subscription:payment.summary.unusedPrice', '차감 금액')}
            </span>
            <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
              - {currencyString + ' '}
              {selectedPlanInfo.salePrice.toLocaleString()}
            </span>
          </div>).otherwise(() => <div className="flex w-full items-center">
            <span className="text-body-2-m font-[500] text-[#27262EE5]">
              {t('subscription:payment.summary.unusedPrice', '차감 금액')}
            </span>
            <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
              - {currencyString + ' '}
              {미사용금액.toLocaleString()}
            </span>
          </div>)}
    </>;
};
const OnetimeUnusedPrice = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    isUpdate,
    selectedPlanInfo,
    currentSpacePlanHashId,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planTemplateType = watch('planTemplateType');
  const {
    미사용금액
  } = useExpectedOnetimePlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_ONETIME,
    spacePlanHashId: currentSpacePlanHashId
  });
  if (미사용금액 <= 0) {
    return null;
  }
  return <>
      <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="SelectedPlanInfo.tsx" />
      <div className="flex w-full items-center">
        <span className="text-body-2-m font-[500] text-[#27262EE5]">
          {t('subscription:payment.summary.unusedPrice', '차감 금액')}
        </span>
        <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
          - {currencyString + ' '}
          {미사용금액.toLocaleString()}
        </span>
      </div>
    </>;
};
const VatRow = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    isUpdate,
    currencyString,
    isKrPayment,
    userCountryCode
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planTemplateType = watch('planTemplateType');
  if (!isUpdate) {
    // 신규 결제는 서버 priceInfo 우선, 구버전 응답이면 preview fallback.
    const vatAmount = selectedPlanInfo.priceInfo?.vatAmount ?? getVatPreview({
      amount: selectedPlanInfo.salePrice,
      currency: isKrPayment ? 'KRW' : 'USD',
      userCountryCode,
      scenario: planTemplateType === PlanTemplateType.PRODUCT_ONETIME ? 'PRODUCT_ONE_TIME_PAYMENT' : 'NEW_SUBSCRIPTION_FIRST_PAYMENT'
    }).vatAmount;
    if (vatAmount <= 0) return null;
    return <VatRowDisplay label={t('subscription:payment.summary.vat', '부가세(VAT 10%)')} currencyString={currencyString} vatAmount={vatAmount} />;
  }
  return planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION ? <SubscriptionVatRow data-sentry-element="SubscriptionVatRow" data-sentry-component="VatRow" data-sentry-source-file="SelectedPlanInfo.tsx" /> : <OnetimeVatRow data-sentry-element="OnetimeVatRow" data-sentry-component="VatRow" data-sentry-source-file="SelectedPlanInfo.tsx" />;
};
const SubscriptionVatRow = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    isUpdate,
    spaceHashId,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planTemplateType = watch('planTemplateType');
  const {
    vatAmount
  } = useExpectedPlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION,
    spaceHashId
  });
  if (!vatAmount || vatAmount <= 0) return null;
  return <VatRowDisplay label={t('subscription:payment.summary.vat', '부가세(VAT 10%)')} currencyString={currencyString} vatAmount={vatAmount} data-sentry-element="VatRowDisplay" data-sentry-component="SubscriptionVatRow" data-sentry-source-file="SelectedPlanInfo.tsx" />;
};
const OnetimeVatRow = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    isUpdate,
    currentSpacePlanHashId,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planTemplateType = watch('planTemplateType');
  const {
    vatAmount
  } = useExpectedOnetimePlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_ONETIME,
    spacePlanHashId: currentSpacePlanHashId
  });
  if (!vatAmount || vatAmount <= 0) return null;
  return <VatRowDisplay label={t('subscription:payment.summary.vat', '부가세(VAT 10%)')} currencyString={currencyString} vatAmount={vatAmount} data-sentry-element="VatRowDisplay" data-sentry-component="OnetimeVatRow" data-sentry-source-file="SelectedPlanInfo.tsx" />;
};
const VatRowDisplay = ({
  label,
  currencyString,
  vatAmount
}: {
  label: string;
  currencyString: string;
  vatAmount: number;
}) => <>
    <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="SelectedPlanInfo.tsx" />
    <div className="flex w-full items-center">
      <span className="text-body-2-m font-[500] text-[#27262EE5]">{label}</span>
      <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
        + {currencyString + ' '}
        {vatAmount.toLocaleString()}
      </span>
    </div>
  </>;